import { Helmet } from 'react-helmet-async';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

import DeleteDialog from 'src/components/pages/DeleteDialog';
import { useGetMatchesQuery } from 'src/api/matches/getMatches';
import { RoutesKeys } from 'src/enums/routesKeys.enum';
import { MatchType } from 'src/types/domain.types';
import { useDeleteMatchMutation } from 'src/api/matches/deleteMatch';
import { CompetitionFormControl } from 'src/components/form/CompetitionFormControl';
import { TableHeaderType } from 'src/types/custom.types';
import { dateUtils } from 'src/utils/date';
import { useTimezoneStore } from 'src/store/timezoneStore';

export default function MatchesPage() {
  const navigate = useNavigate();
  const { timezone } = useTimezoneStore();

  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [deleteMatch, setDeleteMatch] = useState<MatchType | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [competitionId, setCompetitionId] = useState<number>(1); // filter by competitionId, default ID = 1

  const {
    data: matches = [],
    isLoading: isGetMatchesLoading,
    refetch,
  } = useGetMatchesQuery();
  const { mutateAsync: deleteMatchMutation } = useDeleteMatchMutation();

  const handleOpenMenu = (event: any) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const handleEditMatch = (matchId: number) => {
    navigate(RoutesKeys.matchesEdit.replace(':id', matchId.toString()));
  };

  const handleDeleteMatch = (matchId: number) => {
    const match = matches.find((match) => match.match_id === matchId);
    if (match) {
      setOpenId(matchId);
      setDeleteMatch(match);
    }
  };

  const deleteMatches = async () => {
    try {
      await deleteMatchMutation(Number(deleteMatch?.match_id));
      refetch();
    } catch (error) {
      console.log(error);
      Promise.reject(error);
    }
  };

  const tableHeader: TableHeaderType[] = [
    { id: 'match_id', label: 'ID' },
    { id: 'name_en', label: 'Name' },
    { id: 'score', label: 'Score' },
    { id: 'date', label: `Date (${timezone})` },
    { id: 'actions', label: 'Actions', align: 'right' },
  ];

  const matchesByCompetitionId = useMemo(() => {
    return matches.filter(
      (match: MatchType) => match.competition_id === competitionId
    );
  }, [matches, competitionId]);

  return (
    <>
      <Helmet>
        <title> Matches </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Matches
          </Typography>

          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => navigate(RoutesKeys.matchesCreate)}
          >
            New Match
          </Button>
        </Stack>

        <Box sx={{ my: 1 }}>
          <CompetitionFormControl
            value={competitionId}
            onChange={(event: any) => setCompetitionId(event.target.value)}
          />
        </Box>

        <Card>
          {isGetMatchesLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : matchesByCompetitionId.length === 0 ? (
            <Alert severity='info'>No matches found</Alert>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      {tableHeader.map(({ id, label, align = 'left' }) => (
                        <TableCell key={id} align={align}>
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matchesByCompetitionId.map(({ teams, ...match }) => (
                      <TableRow
                        key={match.match_id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          {match.match_id}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          {match.name_en ?? (
                            <span>
                              {teams?.[0]?.name_en ?? '--'}
                              {` (${teams?.[0]?.team_odds})`} vs{' '}
                              {teams?.[1]?.name_en ?? '--'}{' '}
                              {` (${teams?.[1]?.team_odds})`}
                            </span>
                          )}
                        </TableCell>

                        <TableCell component='th' scope='row'>
                          {teams?.[0]?.score ?? '--'} :{' '}
                          {teams?.[1]?.score ?? '--'}
                        </TableCell>

                        <TableCell component='th' scope='row'>
                          {dateUtils.formatForDisplay(
                            match.start_utc,
                            timezone
                          )}
                        </TableCell>

                        <TableCell align='right'>
                          <IconButton
                            size='large'
                            color='inherit'
                            onClick={handleOpenMenu}
                            id={match.match_id.toString()}
                          >
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleEditMatch(Number(openId))}>
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteMatch(Number(openId))}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      {deleteMatch !== null && (
        <DeleteDialog
          openConfirm={true}
          type={'Match'}
          desc={'Are you sure you want to delete this match?'}
          setOpenConfirm={() => setDeleteMatch(null)}
          deleteHandler={deleteMatches}
          id={openId ?? 0}
        />
      )}
    </>
  );
}
